import React, { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { formatPinNumber } from 'utils/personal-identity-number';
import { Box, CircularProgress } from '@mui/material';
import { Order, OrderType } from 'models/order';
import { orderForLabModifyTemplate } from 'apollo/templates/order/query.graphql';
import { LabOrderInput } from 'models/order-input/lab-order';
import { LabOrderContext } from './lab-order-context';
import LabOrder from './lab-order.component';
import { restorationsToLabConstructions } from 'components/order-flow/shared/restoration-input-helper';
import { labClientTemplate } from 'apollo/templates/client-directory';
import { extractOrderInputType } from 'utils/orders';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

const ModifyLabOrder: FC = () => {
    const [order, setOrder] = useState<LabOrderInput>({} as LabOrderInput);
    const [isTioTandvardPricingEnabled, setIsTioTandvardPricingEnabled] = useState<boolean>(false);
    const params = useParams<{ id: string }>();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    const [fetchOrder, { loading }] = useLazyQuery(orderForLabModifyTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ order }: { order: Order }) => {
            if (order) {
                try {
                    setOrder({
                        inputType: extractOrderInputType(order),
                        _id: order._id,
                        supplier: order.supplier,
                        isCreatedByLab: order.isCreatedByLab,
                        isActionBasedOrder: order.type === OrderType.ACTION_BASED,
                        customer: order.customer,
                        historicalComments: order.historicalComments || [],
                        dentist: order.dentist,
                        // DD-3494: a patient-less order prefills no patient, as a fresh wizard starts.
                        patient: order.patient
                            ? {
                                  personalIdentityNumber: formatPinNumber(order.patient.personalIdentityNumber),
                                  firstName: order.patient.firstName,
                                  lastName: order.patient.lastName,
                                  noPersonalIdentityNumber: order.patient.noPersonalIdentityNumber || false,
                              }
                            : undefined,
                        constructions: restorationsToLabConstructions(order),
                        comment: '',
                        files: order.filesUrl || [],
                        sample: order.deliveryDetails.sample || { type: '', content: [], otherContent: '' },
                        sampleReceivedDate: order.manualSelectedReceivedDate
                            ? new Date(order.manualSelectedReceivedDate)
                            : new Date(),
                        deliveryDetails: {
                            date: order.deliveryDetails.date ? new Date(order.deliveryDetails.date) : null,
                            time: {
                                hour: order.deliveryDetails.time?.hour,
                                minute: order.deliveryDetails.time?.minute,
                            },
                            pickUpLocation: order.deliveryDetails.pickUpLocation,
                            destination: order.deliveryDetails.destination,
                        },
                        internalDeliveryDate: order.deliveryDetails.internalDeliveryDate,
                        externalDeliveryDate: order.deliveryDetails.externalDeliveryDate,
                        productionPlanningSteps: (order.productionPlan || []).map((step) => ({
                            stepId: step.stepId,
                            stepName: step.stepName,
                            startDate: step.startDate ? new Date(step.startDate) : null,
                            deadline: step.deadline ? new Date(step.deadline) : null,
                            estimatedProductionHours: step.estimatedProductionHours || 0,
                            estimatedMachineHours: step.estimatedMachineHours || 0,
                            allowOvernight: step.allowOvernight ?? false,
                            startNextDay: step.startNextDay ?? false,
                            stepOrder: step.stepOrder,
                        })),
                        panNumber: order.panNumber || undefined,
                        panNumberDescription: order.panNumberDescription || undefined,
                        internalOrderId: order.internalOrderId || undefined,
                        currency: order.currency,
                    } as LabOrderInput);
                } catch (error) {
                    showBoundary(error);
                    return;
                }
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    // TODO: Look into if theres a better way to get clinics order type (Action-based/Simple)
    const [labClient] = useLazyQuery(labClientTemplate, {
        onCompleted: ({ labClient }) => {
            if (labClient) {
                const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
                    labClient.customer.isMain
                        ? labClient.customer?.customerSettings
                        : labClient.customer?.parentClinic?.customerSettings,
                    CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
                );
                const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
                    currentOrg.customerSettings,
                    CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
                );
                // orderVersion is the backend's resolved answer - parent-clinic inheritance and
                // the local-clinic rule are already applied - so read it instead of re-deriving
                // it from the customer settings.
                const isActionBasedClinic = labClient.customer.orderVersion === CONSTANTS.SETTINGS_VALUES.ACTION_BASED;
                setIsTioTandvardPricingEnabled(
                    isActionBasedClinic ? isTioTandvardPricingEnabled : isTioTandvardPricingForLabEnabled,
                );
                setOrder((prev) => ({
                    ...prev,
                    isActionBasedOrder: isActionBasedClinic,
                }));
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        fetchOrder({ variables: { orderId: params.id } });
    }, [params.id]);

    useEffect(() => {
        if (order.customer?._id) {
            labClient({ variables: { clientId: order.customer._id } });
        }
    }, [order.customer]);

    return (
        <LabOrderContext.Provider
            value={{
                order,
                setOrder,
                isTioTandvardPricingEnabled,
                setIsTioTandvardPricingEnabled,
                isProductionPlanningEnabled,
            }}
        >
            {loading || !order._id ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <Box>
                        <CircularProgress size={80} />
                    </Box>
                </Box>
            ) : (
                <LabOrder />
            )}
        </LabOrderContext.Provider>
    );
};

export default ModifyLabOrder;
